import { DragEvent, useRef } from "react";
import styles from "./camera-order.module.css";
import CameraOrderItem from "./components/camera-order-item/CameraOrderItem";
import { ReactComponent as SaveIcon } from "../../../../components/svg/save-camera-order.svg";
import { Button } from "@mui/material";
import { inject, observer } from "mobx-react";
import { ManageLocationStore } from "../../store/store";
import { useTranslation } from "react-i18next";
import { saveCameras } from "../../web";
import BasicSpinner from "../../../../components/basic-spinner/BasicSpinner";

export type CameraOrderProps = {
    manageLocationsStore?: ManageLocationStore;
};

function CameraOrder(props: CameraOrderProps) {
    const ref = useRef<any>(null);
    const draggingId = useRef<string | null>(null);
    const manageLocationsStore = props.manageLocationsStore!;
    const cameraList = manageLocationsStore.cameraList;
    const loading = manageLocationsStore.loadingCameras;

    const { t } = useTranslation(["manage-locations"]);

    const onDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const y = e.clientY;
        const id = getElementAfterId(y);

        const draggingItemIndex = cameraList.findIndex(
            (item) => Number(item.cameraId) === Number(draggingId.current)
        );

        const newList = [...cameraList];
        const draggingItem = newList[draggingItemIndex];

        newList.splice(draggingItemIndex, 1);

        if (id) {
            const index = newList.findIndex(
                (item) => Number(item.cameraId) === Number(id)
            );
            newList.splice(index + 1, 0, draggingItem);
        } else {
            newList.unshift(draggingItem);
        }

        manageLocationsStore.setCameraList(newList);
    };

    const getElementAfterId = (clientY: number) => {
        let selectedId: string | null = null;
        let diffFromClientY = Number.POSITIVE_INFINITY;

        [...ref.current.children].map((item: any) => {
            if (Number(item.id) !== Number(draggingId.current)) {
                const box = item.getBoundingClientRect();
                const boxDiff = clientY - (box.top + box.height / 2);

                if (boxDiff > 0 && boxDiff < diffFromClientY) {
                    diffFromClientY = boxDiff;
                    selectedId = item.id;
                }
            }
        });

        return selectedId;
    };

    const cameras = () => {
        return (
            <>
                {cameraList.map((item) => {
                    return (
                        <CameraOrderItem
                            id={item.cameraId}
                            cameraName={item.name}
                            dragged={item.cameraId === draggingId.current}
                            onDragStart={() => {
                                draggingId.current = item.cameraId;
                            }}
                            onDragEnd={() => {
                                draggingId.current = null;
                                manageLocationsStore.setCameraList([
                                    ...cameraList,
                                ]);
                            }}
                        />
                    );
                })}
            </>
        );
    };

    const content = () => {
        if (loading)
            return (
                <div className={styles["camera-spinner"]}>
                    <BasicSpinner />
                </div>
            );

        return cameras();
    };

    const saveCameraOrder = () => {
        const cameraList = manageLocationsStore.cameraList.map(
            (camera, index) => {
                return { ...camera, rank: index + 1 };
            }
        );

        saveCameras(manageLocationsStore.selectedLocationId, cameraList);
    };

    const render = () => {
        return (
            <div className={styles["camera-order"]}>
                <div className={styles["header"]}>
                    <div className={styles["save-button"]}>
                        <Button
                            sx={{
                                padding: "6px",
                                backgroundColor: "white",
                                color: "#173544",
                                border: "1px solid rgba(23, 53, 68, 0.60)",
                            }}
                            disableElevation={true}
                            startIcon={<SaveIcon />}
                            fullWidth
                            variant="contained"
                            onClick={saveCameraOrder}
                        >
                            {t("SaveCameraOrder")}
                        </Button>
                    </div>
                </div>
                <div
                    className={styles["list-container"]}
                    onDragOver={onDragOver}
                    ref={ref}
                >
                    {content()}
                </div>
            </div>
        );
    };

    return render();
}

export default inject("manageLocationsStore")(observer(CameraOrder));
